// src/encounter/services/use-chief-complaint.ts
// Pre-encounter chief-complaint quick-capture — the Flutter-parity endpoint set, wired live
// per Samer's live-review verdict 2026-07-23 ("chief complaint and the RAG of the chief
// complaint are not wired") — PROD-1588. All three endpoints live-verified 200 on dev-api
// this session (encounter dc6a6ac8…, patient 2a3ef273…):
//
//   · suggestions — GET /api/AISearch/Search?query= (Scrips.Master AISearchController →
//     ChiefComplaintService.SearchComplaintsForUIAsync: SQL catalogue first, Azure vector
//     search fallback — THE chief-complaint RAG index). Flutter twin: fetchAIChiefComplaints
//     (common_data_source_impl.dart:1548 → masterAPI(MasterService.AI)/Search). PascalCase
//     wire (Scrips.Master has no camelCase policy — verified live).
//   · persist — POST /api/Patients/CreateChiefComplaint (PatientsController:1631). The
//     handler is a REPLACE-ALL upsert per (encounter, subject): soft-deletes every existing
//     PatientHpi row, re-inserts the posted list — so every save posts the FULL list, and
//     kept complaints must round-trip their patientHpi children or they'd be dropped
//     (CreatePatientComplaintCommand.cs — read this session). Flutter twin:
//     createPatientComplaints (encounter_data_source_impl.dart:119).
//   · re-read — GET /api/Patients/GetPatientComplaint?patientId=&encounterId= →
//     PatientComplaintModel (camelCase; complaints carry the RAG-enhanced templateItem
//     sections on read — read-side only, ignored on write).
//
// DIVERGENCE-REGISTER NOTE (PROD-1159 / PROD-1354c): this is the PRE-ENCOUNTER quick-CC
// seam — the open product question flagged on PROD-1159 ("does a pre-encounter quick-CC
// entry survive at all?"), which Samer's 2026-07-23 verdict resolves as YES. The in-encounter
// HPI questionnaire persistence is UNTOUCHED: the FHIR QuestionnaireResponse upsert remains
// the sole persist for questionnaire ANSWERS; this writes only the complaint list (no
// TS-authored linkIds anywhere near the legacy GUID-itemId shape that killed the dual-write).
import { useMutation, useQuery } from '@tanstack/react-query';
import { get, post } from '@/shared/api/api-client';
import type { PatientComplaintModel } from '../lib/to-chief-complaint';
import { useNoteWrite } from './note-session';

const MIN_QUERY_LENGTH = 2;

/** One RAG suggestion row — Scrips.Master ComplaintSearchResult (PascalCase wire). */
export interface AiChiefComplaintSuggestion {
  ComplaintId: string;
  ComplaintName: string | null;
  CategoryId: string;
  CategoryName: string | null;
  IsPrimaryCategory: boolean;
  Description: string | null;
}

/**
 * Chief-complaint RAG suggestions (min 2 chars; consumer debounces — appointments pattern).
 *
 * `placeholderData: (prev) => prev` is what stops the suggestion list FLICKERING while you type.
 * Reported by Andrew on dev, 2026-08-25: writing free text in the chief complaint, the suggestion
 * "appears and disappear twice then appear". Each debounced query is its own cache key, so without
 * this the list has no `data` at all while the next request is in flight: results → blank → results,
 * once per debounce cycle. Typing a phrase crosses several cycles, which is the "twice".
 *
 * It only changes WHICH `data` is exposed during a fetch — the request, the `enabled` gate and the
 * signal-based cancellation are untouched, so a stale response still cannot overwrite a newer one.
 *
 * This is the convention every other type-ahead in the repo already follows (allergy-code,
 * diagnosis-code, referral, medication-valueset, claim-diagnosis searches); this hook was the one
 * that missed it. Deliberately NOT added to `useAiDiagnosisSearch`, which fires on an explicit
 * Suggest tap rather than per keystroke and so cannot flicker this way.
 */
export function useChiefComplaintSuggestions(query: string) {
  const q = query.trim();
  return useQuery<AiChiefComplaintSuggestion[]>({
    queryKey: ['chief-complaint-suggestions', q],
    enabled: q.length >= MIN_QUERY_LENGTH,
    placeholderData: (prev) => prev,
    queryFn: async ({ signal }) => {
      const res = await get<AiChiefComplaintSuggestion[]>(
        `/api/AISearch/Search?query=${encodeURIComponent(q)}`,
        { signal },
      );
      return res ?? [];
    },
  });
}

/**
 * Imperative one-shot fetch of the SAME RAG suggestions `useChiefComplaintSuggestions` fetches
 * reactively — for a caller that isn't a component render, such as the DS `ChiefComplaintHpiBlock`
 * picker's `onSearch: (query) => Promise<readonly string[]>` (PROD-2577 part B). Not a second
 * endpoint or a second RAG index: same `GET /api/AISearch/Search`, same min-length guard.
 */
export async function fetchChiefComplaintSuggestions(query: string): Promise<AiChiefComplaintSuggestion[]> {
  const q = query.trim();
  if (q.length < MIN_QUERY_LENGTH) return [];
  const res = await get<AiChiefComplaintSuggestion[]>(`/api/AISearch/Search?query=${encodeURIComponent(q)}`);
  return res ?? [];
}

/** The complaint document's URL. `mappedOnly` (PROD-2947 · frame v2 only; Scrips.Patient #1040, PROD-3048): a
 *  complaint with no HPI complaint mapping gets NO attribute groups instead of the whole Default catalogue. Absent,
 *  the URL is exactly the one every other caller (and Flutter) sends. A backend without #1040 ignores the parameter
 *  and returns the catalogue as before, so sending it early changes nothing. */
export function patientComplaintUrl(patientId: string, encounterId: string, mappedOnly = false): string {
  const base = `/api/Patients/GetPatientComplaint?patientId=${encodeURIComponent(patientId)}&encounterId=${encodeURIComponent(encounterId)}`;
  return mappedOnly ? `${base}&mappedOnly=true` : base;
}

/** The persisted complaint document for this encounter (`null` complaints → empty list). */
export function usePatientComplaint(
  patientId: string | null | undefined,
  encounterId: string | null | undefined,
  opts: { mappedOnly?: boolean } = {},
) {
  const mappedOnly = opts.mappedOnly === true;
  return useQuery<PatientComplaintModel>({
    // The mapped read is its own cache entry (a different payload); the OFF key is unchanged, and a prefix
    // invalidation of ['patient-complaint', patientId, encounterId] still reaches both.
    queryKey: mappedOnly ? ['patient-complaint', patientId, encounterId, 'mapped-only'] : ['patient-complaint', patientId, encounterId],
    enabled: !!patientId && !!encounterId,
    queryFn: async ({ signal }) => {
      const res = await get<PatientComplaintModel>(patientComplaintUrl(patientId as string, encounterId as string, mappedOnly), { signal });
      return res ?? { encounter: encounterId as string, subject: patientId as string, complaints: [] };
    },
  });
}

/**
 * Replace-all persist. Deliberately does NOT invalidate the read — the caller awaits its own
 * `complaintQuery.refetch()` before handing the chips back from its optimistic list, which is
 * deterministic where a fire-and-forget invalidation was not (PROD-2030; see the body note).
 */
export function useSaveChiefComplaint() {
  const noteWrite = useNoteWrite();
  return useMutation<unknown, Error, PatientComplaintModel>({
    mutationFn: (body) => noteWrite(() => post<unknown>('/api/Patients/CreateChiefComplaint', body)),
    // NO cache invalidation here, deliberately. It used to `invalidateQueries` the complaint
    // document, which is fire-and-forget: the refetch was still in flight when the caller handed
    // the chips back from its optimistic list to `complaintQuery.data`, so they fell back to the
    // STALE document and the just-added complaint vanished for one round trip — added, removed,
    // added (Andrew, dev 2026-08-25; the same in reverse when removing with the x).
    // The caller now awaits its own refetch before handing over, which is deterministic. Keeping an
    // invalidate here as well would just fire a second GET for the same document.
  });
}
